import { useState } from 'react';
import { Permission, encodePermissions } from '@callzone/shared';
import { api } from '../lib/api';
import { useActiveGuild, useStore } from '../store';
import { LockIcon } from './icons';

interface Props {
  type: 'text' | 'voice';
  onClose: () => void;
}

export function ChannelModal({ type, onClose }: Props) {
  const guild = useActiveGuild();
  const toast = useStore((s) => s.toast);
  const selectChannel = useStore((s) => s.selectChannel);
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [userLimit, setUserLimit] = useState(0);
  const [privado, setPrivado] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!guild) return null;

  async function create() {
    setBusy(true);
    try {
      const { channel } = await api.createChannel(guild!.guild.id, {
        name: name.trim(),
        type,
        topic: topic.trim() || undefined,
        userLimit: type === 'voice' ? userLimit : undefined,
      });

      /*
       * Canal privado = tirar VIEW_CHANNEL e CONNECT do @everyone. Quem tiver
       * um cargo com essas permissoes concedidas no canal continua entrando, e
       * quem pode mover membros consegue puxar alguem para dentro.
       */
      if (privado) {
        const everyone = guild!.roles.find((r) => r.isDefault);
        if (everyone) {
          const deny = Permission.VIEW_CHANNEL | Permission.CONNECT;
          await api.setOverwrite(channel.id, everyone.id, '0', encodePermissions(deny));
        }
      }

      selectChannel(channel.id);
      toast(`Canal ${channel.name} criado${privado ? ' como privado' : ''}.`);
      onClose();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Falha ao criar canal.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 430 }}>
        <div className="modal-header">
          <h2>Novo canal de {type === 'text' ? 'texto' : 'voz'}</h2>
          <button className="icon-btn" onClick={onClose}>
            {'✕'}
          </button>
        </div>

        <div className="modal-body">
          <div className="field">
            <label>Nome</label>
            <input
              value={name}
              autoFocus
              maxLength={48}
              placeholder={type === 'text' ? 'assuntos-gerais' : 'Sala de jogos'}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && name.trim() && void create()}
            />
          </div>

          {type === 'text' && (
            <div className="field">
              <label>Assunto (opcional)</label>
              <input
                value={topic}
                maxLength={256}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Aparece no topo do canal"
              />
            </div>
          )}

          {type === 'voice' && (
            <div className="field">
              <label>Limite de pessoas</label>
              <select value={userLimit} onChange={(e) => setUserLimit(Number(e.target.value))}>
                <option value={0}>Sem limite</option>
                {[2, 4, 6, 8, 12, 16, 25].map((value) => (
                  <option key={value} value={value}>
                    {value} pessoas
                  </option>
                ))}
              </select>
              <div className="hint">
                Quem tiver permissao de mover membros consegue entrar mesmo com o canal cheio.
              </div>
            </div>
          )}

          <div className="field">
            <div
              className="switch-row"
              role="switch"
              aria-checked={privado}
              tabIndex={0}
              onClick={() => setPrivado((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPrivado((v) => !v);
                }
              }}
            >
              <span className="switch-label">
                <LockIcon
                  style={{ width: 14, height: 14, verticalAlign: -2, marginRight: 6 }}
                />
                Canal privado
              </span>
              <span className={`switch ${privado ? 'on' : ''}`} />
            </div>
            {privado && (
              <div className="hint">
                Fica escondido de quem so tem o cargo @everyone. Você continua podendo puxar
                alguém para dentro pela lista de membros.
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn" disabled={busy || name.trim().length < 1} onClick={() => void create()}>
            Criar canal
          </button>
        </div>
      </div>
    </div>
  );
}
